import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SERVICES } from '../data/services';
import SEO, { SITE, breadcrumb } from '../components/SEO';

const ServicesPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-white pt-16 sm:pt-20">
      <SEO
        path="/services"
        title="Cybersecurity Services | Kennyvicops Cyber Solutions Inc."
        description="Six practical cybersecurity services for small and growing businesses: security assessment, vulnerability management, identity & access, endpoint & network, data protection, and awareness training."
        jsonLd={[
          breadcrumb([
            { name: 'Home', path: '/' },
            { name: 'Services', path: '/services' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'Kennyvicops Cybersecurity Services',
            itemListElement: SERVICES.map((s, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              url: `${SITE}/services/${s.slug}`,
              name: s.name,
            })),
          },
        ]}
      />

      {/* HERO — dark navy */}
      <section className="relative overflow-hidden bg-[#06101F] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(22,135,255,0.16),transparent_55%)]" />
        <div className="absolute inset-0 bg-kv-grid opacity-60" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-14 sm:pt-24 pb-16 sm:pb-20">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2.5 mb-4">
              <span className="w-6 h-px bg-cyan-300/70" />
              <span className="text-[11px] uppercase tracking-[0.22em] text-cyan-300/90 font-semibold">Our Services</span>
            </div>
            <h1 className="font-editorial text-4xl sm:text-6xl lg:text-[4.25rem] font-semibold leading-[1.05] tracking-tight text-white">
              Six practical services. One clear approach.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-300/95 leading-relaxed max-w-3xl">
              We focus on the security work that consistently reduces risk for small and growing
              businesses &mdash; without over-engineering it.
            </p>
          </div>
        </div>
      </section>

      {/* SERVICES GRID — soft off-white */}
      <section className="relative bg-[#FAFBFD] bg-kv-grid-light text-[#101828] border-y border-slate-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-24">
          <div className="grid gap-5 sm:gap-6 md:grid-cols-2">
            {SERVICES.map((s, i) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`services-list-${s.slug}`}
                className="group bg-white border border-slate-200 hover:border-blue-500/50 rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-900/5 flex flex-col"
              >
                <div className="relative aspect-[16/8] overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-white/95 via-white/40 to-transparent" />
                  <div className={`absolute top-5 left-5 inline-flex w-11 h-11 items-center justify-center rounded-xl bg-gradient-to-br ${s.accent} shadow-lg shadow-blue-500/20`}>
                    <s.icon className="w-5 h-5 text-white" />
                  </div>
                  <span className="absolute top-5 right-5 font-editorial text-2xl text-[#06101F]/80 font-semibold">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                </div>
                <div className="p-7 sm:p-8 flex flex-col flex-1">
                  <div className="flex items-start justify-between mb-2 gap-3">
                    <h2 className="font-editorial text-xl sm:text-2xl font-semibold text-[#06101F] leading-snug">{s.name}</h2>
                    <ArrowUpRight className="w-5 h-5 text-slate-400 group-hover:text-blue-600 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                  </div>
                  <p className="text-sm text-blue-700 mb-3 font-medium">{s.headline}</p>
                  <p className="text-sm sm:text-[15px] text-[#475467] leading-relaxed mb-6 flex-1">
                    {s.description}
                  </p>
                  <span className="mt-auto pt-5 border-t border-slate-100 inline-flex items-center gap-1 text-sm font-medium text-blue-600">
                    Learn more <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA — dark navy */}
      <section className="relative overflow-hidden bg-[#06101F] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(22,135,255,0.22),transparent_50%)]" />
        <div className="absolute inset-0 bg-kv-grid opacity-70" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-20 sm:py-24 text-center">
          <h2 className="font-editorial text-3xl sm:text-5xl font-semibold leading-[1.1] text-white">
            Not sure which service fits your situation?
          </h2>
          <p className="mt-5 text-slate-300/95 text-base sm:text-lg max-w-2xl mx-auto">
            Start with a conversation. We&apos;ll help you figure out the best next step.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/contact">
              <Button
                data-testid="services-cta"
                className="px-8 py-6 bg-white text-[#06101F] hover:bg-cyan-100 rounded-full font-semibold"
              >
                Request a Security Consultation <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/security-health-check">
              <Button
                variant="outline"
                data-testid="services-healthcheck-cta"
                className="px-8 py-6 bg-transparent border-cyan-300/50 text-cyan-100 hover:bg-white/5 hover:text-white rounded-full"
              >
                Take the Free Health Check
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
